<template>
  <v-navigation-drawer
    v-model="show"
    v-lock-scroll="show"
    app
    temporary
    right
    width="800px"
    class="right-side-panel create-campaign-advanced-options-panel"
  >
    <panel-header :show.sync="show">
      Advanced options
    </panel-header>

    <cg-form ref="form" class="right-side-panel__content-wrapper">
      <section class="create-campaign-advanced-options-panel__section">
        <h3>Email content</h3>

        <cg-input
          v-model="emailSubjectLine"
          :validation="[checkIfStringContainsNotAllowedTag]"
        >
          <template #label>
            <div class="create-campaign-advanced-options-panel__label-with-tooltip">
              Email subject line

              <common-tooltip
                right
                :top="false"
                :max-width="null"
                :z-index="8"
                :activator-size="20"
                activator-color="#62646A"
              >
                <img alt="" :src="getProductImageBySlug('campaigns_edit_email_subject')" />
              </common-tooltip>

              <button
                v-if="emailSubjectLine !== defaultEmailSubjectLine"
                class="ml-auto"
                @click.prevent="resetEmailSubjectLine"
              >
                Reset
              </button>
            </div>
          </template>
        </cg-input>

        <div class="create-campaign-advanced-options-panel__email-body">
          <label class="create-campaign-advanced-options-panel__label-with-tooltip">
            Email body text

            <common-tooltip
              right
              :top="false"
              :max-width="null"
              :z-index="8"
              :activator-size="20"
              activator-color="#62646A"
            >
              <img alt="" width="500" height="303" :src="emailBodyTooltipImage" />
            </common-tooltip>
          </label>

          <create-campaign-email-body-editor v-model="emailBodyText" />
        </div>

        <cg-input v-model="giftGiverEmail" placeholder="name@company.com" :validation="[email()]">
          <template #label>
            <div class="create-campaign-advanced-options-panel__label-with-tooltip">
              Gift giver email

              <common-tooltip
                right
                :top="false"
                :max-width="360"
                :z-index="8"
                :activator-size="20"
                activator-color="#62646A"
              >
                The sender email address recipients see in the gift email.
              </common-tooltip>
            </div>
          </template>
        </cg-input>

        <cg-input
          v-model="mainButtonLabel"
          :validation="[checkIfStringContainsNotAllowedTag]"
        >
          <template #label>
            <div class="create-campaign-advanced-options-panel__label-with-tooltip">
              Main button label

              <common-tooltip
                right
                :top="false"
                :max-width="null"
                :z-index="8"
                :activator-size="20"
                activator-color="#62646A"
              >
                <img alt="" width="500" height="265" :src="emailButtonTooltipImage" />
              </common-tooltip>
            </div>
          </template>
        </cg-input>

        <div class="create-campaign-advanced-options-panel__cover-image">
          <div class="create-campaign-advanced-options-panel__label-with-tooltip">
            Cover image

            <common-tooltip
              right
              :top="false"
              :max-width="null"
              :z-index="8"
              :activator-size="20"
              activator-color="#62646A"
            >
              <img alt="" width="460" height="450" :src="emailCoverTooltipImage" />
            </common-tooltip>
          </div>

          <create-campaign-cover-image-upload v-model="cover" />
        </div>
      </section>

      <hr>

      <section class="create-campaign-advanced-options-panel__section">
        <h3>Delivery &amp; redemption</h3>

        <cg-input v-model="videoGreeting" placeholder="https://" :validation="[videoGreetingValidation]">
          <template #label>
            <div class="create-campaign-advanced-options-panel__label-wrapper">
              <div class="create-campaign-advanced-options-panel__label-with-tooltip">
                Video greeting URL (optional)

                <common-tooltip
                  right
                  :top="false"
                  :max-width="null"
                  :z-index="8"
                  :activator-size="20"
                  activator-color="#62646A"
                >
                  <img alt="" :src="getProductImageBySlug('campaigns_video_greeting_link')" />
                </common-tooltip>
              </div>

              <button v-if="videoGreeting" @click.prevent="previewVideoURL">
                Preview
              </button>
            </div>
          </template>
        </cg-input>

        <div class="create-campaign-advanced-options-panel__expiration-input">
          <label for="egift-expiration-time">eGift will expire after</label>
          <div>
            <cg-input
              v-model.number="expirationDays"
              type="number"
              min="1"
              max="400"
              step="1"
              :width="80"
              :validation="[required(), campaignExpirationDaysValidation]"
              id="egift-expiration-time"
            />

            days
          </div>
        </div>
      </section>

      <hr>

      <section class="create-campaign-advanced-options-panel__section">
        <h3>Recipient options</h3>

        <div v-if="userIsCgElite" class="create-campaign-advanced-options-panel__shipping">
          <cg-select
            v-model="shipping"
            :items="shippingOptions"
          >
            <template #label>Campaign can be sent to:</template>
          </cg-select>

          <span class="create-campaign-advanced-options-panel__shipping-hint">
            US & Canada / International is only applied if at least one gift option in this campaign is eligible for that shipping.
          </span>
        </div>

        <cg-checkbox v-model="allowExchange">
          Allow recipients to upgrade the gift
        </cg-checkbox>

        <cg-checkbox v-model="addButtonLink">
          Add a call-to-action button after redemption
        </cg-checkbox>

        <v-expand-transition>
          <div v-if="addButtonLink" class="create-campaign-advanced-options-panel__nested-inputs">
            <cg-input
              v-model="buttonLinkLabel"
              placeholder="E.g. Book a meeting"
              :validation="[required()]"
            >
              <template #label>Button label</template>
            </cg-input>

            <cg-input v-model="buttonLinkUrl" placeholder="https://" :validation="[required()]">
              <template #label>Button URL</template>
            </cg-input>
          </div>
        </v-expand-transition>

        <cg-checkbox v-model="disableThankYouNotes">
          <div class="create-campaign-advanced-options-panel__thank-you-checkbox">
            Don’t allow recipient <b>thank you notes</b>
          </div>
        </cg-checkbox>

        <cg-checkbox v-model="disableReminderEmails">
          Disable reminder emails to recipients
        </cg-checkbox>

        <cg-checkbox v-model="disableAlternativeGifts" class="mb-0">
          Show alternative gifts in case item/s out of stock
        </cg-checkbox>
      </section>

      <!-- Tags endpoints require auth; a guest's 401 would log them out -->
      <template v-if="userIsLoggedIn">
        <hr>

        <section class="create-campaign-advanced-options-panel__section">
          <h3>Tags</h3>

          <assign-campaign-tags v-model="assignedTags" />
        </section>
      </template>

      <hr>

      <section class="create-campaign-advanced-options-panel__section">
        <div class="create-campaign-advanced-options-panel__status-switch">
          <common-switch v-model="isActive" id="campaign-status-switch" />
          <label for="campaign-status-switch">
            <strong>Status:</strong> {{ isActive ? 'Active' : 'Disabled' }}
          </label>
        </div>
      </section>

      <cg-button class="create-campaign-advanced-options-panel__done-button" @click="show = false">
        Done
      </cg-button>
    </cg-form>
  </v-navigation-drawer>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import userName from '@/mixins/userName'
import companyName from '@/mixins/companyName'
import userIsCgElite from '@/mixins/userIsCgElite'
import userIsLoggedIn from '@/mixins/userIsLoggedIn'
import getProductImageBySlug from '@/mixins/getProductImageBySlug'
import checkIfStringContainsNotAllowedTag from '@/components/createCampaign/mixins/checkIfStringContainsNotAllowedTag'

import { CgInput, CgSelect, CgCheckbox, CgForm, CgButton } from '@corporategiftcom/design-system'
import { required, email } from '@corporategiftcom/design-system/validations'
import AssignCampaignTags from '@/components/createCampaign/TheAssignCampaignTagsCombobox.vue'
import CreateCampaignCoverImageUpload from '@/components/createCampaign/CreateCampaignCoverImageUpload.vue'
import CreateCampaignEmailBodyEditor from '@/components/createCampaign/CreateCampaignEmailBodyEditor.vue'
import PanelHeader from '@/components/PanelHeader.vue'

const { mapState, mapActions, mapMutations } = createNamespacedHelpers('newCampaign')

import campaignExpirationDaysValidation from '@/utils/campaignExpirationDaysValidation'
import videoGreetingValidation from '@/utils/videoGreetingValidation'

const SHIPPING_OPTIONS = [
  { text: 'US Only', value: 'us_only' },
  { text: 'US & Canada', value: 'us_canada' },
  { text: 'International', value: 'international' },
]

export default {
  name: 'CreateCampaignAdvancedOptionsPanel',
  mixins: [
    userName,
    companyName,
    userIsCgElite,
    userIsLoggedIn,
    getProductImageBySlug,
    checkIfStringContainsNotAllowedTag,
  ],
  components: {
    PanelHeader,
    CgForm,
    CgButton,
    CgInput,
    CgSelect,
    CgCheckbox,
    AssignCampaignTags,
    CreateCampaignCoverImageUpload,
    CreateCampaignEmailBodyEditor,
  },
  props: {
    value: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    shippingOptions: SHIPPING_OPTIONS,
  }),
  computed: {
    ...mapState(['form']),
    show: {
      get () { return this.value },
      set (val) { this.$emit('input', val) },
    },
    campaignId () { return this.$route.params.campaignId ?? 'create' },
    isEditingCampaign () { return this.campaignId !== 'create' },
    expirationDays: {
      get () { return this.form.advancedOptions.expirationDays },
      set (val) { this.setExpirationDays(val) },
    },
    emailSubjectLine: {
      get () { return this.form.advancedOptions.emailSubjectLine },
      set (val) { this.setEmailSubjectLine(val) },
    },
    emailBodyText: {
      get () { return this.form.advancedOptions.emailBodyText },
      set (val) { this.setEmailBodyText(val) },
    },
    giftGiverEmail: {
      get () { return this.form.advancedOptions.giftGiverEmail },
      set (val) { this.setGiftGiverEmail(val) },
    },
    mainButtonLabel: {
      get () { return this.form.advancedOptions.mainButtonLabel },
      set (val) { this.setMainButtonLabel(val) },
    },
    cover: {
      get () { return this.form.advancedOptions.cover },
      set (val) { this.setCover(val) },
    },
    shipping: {
      get () { return this.form.advancedOptions.shipping },
      set (val) { this.setShipping(val) },
    },
    videoGreeting: {
      get () { return this.form.advancedOptions.videoGreeting },
      set (val) { this.setVideoGreeting(val) },
    },
    allowExchange: {
      get () { return this.form.advancedOptions.allowExchange },
      set (val) { this.setAllowExchange(val) },
    },
    disableThankYouNotes: {
      get () { return this.form.advancedOptions.disableThankYouNotes },
      set (val) { this.setDisableThankYouNotes(val) },
    },
    disableReminderEmails: {
      get () { return this.form.advancedOptions.disableReminderEmails },
      set (val) { this.setDisableReminderEmails(val) },
    },
    disableAlternativeGifts: {
      get () { return this.form.advancedOptions.disableAlternativeGifts },
      set (val) { this.setDisableAlternativeGifts(val) },
    },
    addButtonLink: {
      get () { return this.form.advancedOptions.successButton.addButton },
      set (val) { this.setAddSuccessButton(val) },
    },
    buttonLinkUrl: {
      get () { return this.form.advancedOptions.successButton.buttonURL },
      set (val) { this.setSuccessButtonURL(val) },
    },
    buttonLinkLabel: {
      get () { return this.form.advancedOptions.successButton.buttonLabel },
      set (val) { this.setSuccessButtonLabel(val) },
    },
    isActive: {
      get () { return this.form.isActive },
      set (val) { this.setIsActive(val) },
    },
    assignedTags: {
      get () { return this.form.tags },
      set (val) { this.setTags(val) },
    },
    defaultEmailSubjectLine () {
      const { companyName, userIsCgElite, userFullName } = this;

      if (userIsCgElite && companyName) { return `${companyName} - Sent You a Gift!` }
      if (userFullName) { return `${userFullName} - Sent You a Gift!` }
      return 'You received a gift!'
    },
    defaultEmailBodyText () {
      const senderName = this.form.displayName || this.userFullName
      const orgNotificationText = this.$store.state.header?.headerData?.egiftNotificationText

      if (orgNotificationText) {
        return orgNotificationText
          .replace(/\{\{var senderFullName\}\}/g, senderName || '')
          .replace(/<br\s*\/?>/gi, ' ')
          .trim()
      }

      return senderName
        ? `Surprise! You've received a gift from ${senderName}`
        : "Surprise! You've received a gift!"
    },
    defaultGiftGiverEmail () {
      return this.userEmail
    },
    defaultMainButtonLabel () {
      return this.form.selectedProducts.length === 1 ? 'Get your gift' : 'Choose your gift'
    },
    userEmail () { return this.$store.state.header?.headerData?.customerEmail ?? null },
    emailBodyTooltipImage () { return require('@/assets/createCampaign/email-body.png') },
    emailButtonTooltipImage () { return require('@/assets/createCampaign/email-button.png') },
    emailCoverTooltipImage () { return require('@/assets/createCampaign/email-cover.png') },
  },
  created () {
    this.setDefaultEmailSubjectLine();
    this.setDefaultEmailBodyText();
    this.setDefaultGiftGiverEmail();
    this.setDefaultMainButtonLabel();

    // Editing an existing campaign already loads this via loadCampaignData; a brand-new
    // campaign never dispatches it otherwise, leaving the tags dropdown empty.
    if (!this.isEditingCampaign) { this.getCampaignsTags() }
  },
  methods: {
    required,
    email,
    campaignExpirationDaysValidation,
    videoGreetingValidation,
    ...mapActions(['getCampaignsTags']),
    ...mapMutations([
      'setTags',
      'setIsActive',
      'setCover',
      'setShipping',
      'setAllowExchange',
      'setVideoGreeting',
      'setExpirationDays',
      'setEmailBodyText',
      'setGiftGiverEmail',
      'setMainButtonLabel',
      'setAddSuccessButton',
      'setEmailSubjectLine',
      'setSuccessButtonURL',
      'setSuccessButtonLabel',
      'setDisableThankYouNotes',
      'setDisableReminderEmails',
      'setDisableAlternativeGifts',
    ]),
    setDefaultEmailSubjectLine () {
      const { emailSubjectLine, defaultEmailSubjectLine } = this;
      if (emailSubjectLine === null) { this.emailSubjectLine = defaultEmailSubjectLine; }
    },
    setDefaultEmailBodyText () {
      if (this.emailBodyText === null) { this.emailBodyText = this.defaultEmailBodyText; }
    },
    setDefaultGiftGiverEmail () {
      if (this.giftGiverEmail === null) { this.giftGiverEmail = this.defaultGiftGiverEmail; }
    },
    setDefaultMainButtonLabel () {
      if (this.mainButtonLabel === null) { this.mainButtonLabel = this.defaultMainButtonLabel; }
    },
    previewVideoURL () {
      this.$store.dispatch('app/handleVideoModal', {
        show: true,
        url: this.videoGreeting,
      })
    },
    resetEmailSubjectLine () {
      this.emailSubjectLine = this.defaultEmailSubjectLine
    },
    validate () {
      const result = this.$refs.form.validate();

      if (!result) { this.show = true }
      return result;
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/css/common/right-side-panel.scss';

.create-campaign-advanced-options-panel {
  & hr {
    width: 100%;
    border: none;
    height: 1px;
    background-color: #D3D2D2;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;

    & > h3 {
      font-family: 'Lato-Bold', sans-serif;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      color: #95979D;
    }
  }

  &__label-wrapper {
    gap: 4px;
    display: flex;
    flex-wrap: nowrap;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    font-family: 'Lato', sans-serif;
    font-weight: 700;
    font-size: 15px;
    line-height: 18px;
    color: #222325;
    text-align: left;

    & > button {
      color: #6E50FF;
      font-family: 'Lato-Regular', sans-serif;
      font-weight: 400;
    }
  }

  &__label-with-tooltip {
    display: flex;
    align-items: center;
    flex-direction: row;
    flex-wrap: nowrap;
    line-height: 20px;
    gap: 8px;

    font-family: 'Lato', sans-serif;
    font-weight: 700;
    font-size: 15px;
    color: #222325;
    text-align: left;

    & > button {
      color: #6E50FF;
      font-family: 'Lato-Regular', sans-serif;
      font-weight: 400;
    }
  }

  &__email-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__cover-image {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__expiration-input {
    display: flex;
    flex-direction: column;
    gap: 10px;

    & > label {
      font-family: 'Lato', sans-serif;
      font-weight: 700;
      font-size: 15px;
      line-height: 18px;
      color: #222325;
      text-align: left;
    }

    & > div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 10px;
    }
  }

  &__nested-inputs {
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: 24px;
  }

  &__thank-you-checkbox {
    font-family: 'Lato', sans-serif;
    font-weight: 400;
    font-size: 15px;
    line-height: 18px;

    & > b {
      font-family: 'Lato-Bold', sans-serif;
      font-style: italic;
      font-weight: 700;
    }
  }

  &__done-button {
    align-self: flex-end;
    border-radius: 100px;
  }

  &__shipping {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__shipping-hint {
    font-family: 'Lato-Italic', sans-serif;
    font-size: 14px;
    color: #95979D;
  }

  &__status-switch {
    display: flex;
    align-items: center;
    gap: 12px;

    & > label {
      font-weight: 700;
      font-size: 14px;
      color: #62646A;

      & > strong {
        font-family: 'Lato-Bold', sans-serif;
        font-size: 15px;
        color: #222325;
      }
    }
  }
}
</style>
